// «Моё местоположение» на карте /tutors (решение владельца 12.10): родитель
// видит себя и кто рядом.
//
// ПРИВАТНОСТЬ. Координаты родителя живут только в браузере: на наш сервер не
// уходят, в аналитику не пишутся. Метка «Evim» (адрес или точка на карте)
// хранится в localStorage этого браузера, чтобы остаться к следующему визиту,
// и стирается кнопкой «Sil». Геоточка с телефона не хранится вовсе.
// Google получает точку только для района (Places, как форма репетитора) — он
// и так рисует эту карту.

import type { PublicPartner } from "@/lib/map-public.functions";
import { DISTRICTS } from "@/data/map-taxonomy";

export type Spot = { lat: number; lng: number };
/** Где родитель: геоточка (с точностью, м) или его метка «Evim». */
export type Me = Spot & { kind: "gps" | "home"; accuracy: number | null };

/** Точнее этого — «рядом» уже не обещаем (типично для компьютера без GPS). */
export const LOW_ACCURACY_M = 1000;
export const HOME_STORAGE_KEY = "yt_map_home";

export function readHome(): Spot | null {
  try {
    const raw = window.localStorage.getItem(HOME_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Spot>;
    return typeof v.lat === "number" && typeof v.lng === "number" && Number.isFinite(v.lat) && Number.isFinite(v.lng)
      ? { lat: v.lat, lng: v.lng }
      : null;
  } catch {
    return null;
  }
}
export function saveHome(p: Spot): void {
  try {
    // Только координаты — ни адреса, ни времени.
    window.localStorage.setItem(HOME_STORAGE_KEY, JSON.stringify({ lat: p.lat, lng: p.lng }));
  } catch {
    /* хранилище недоступно — метка живёт до закрытия страницы */
  }
}
export function clearHome(): void {
  try {
    window.localStorage.removeItem(HOME_STORAGE_KEY);
  } catch {
    /* нечего стирать */
  }
}

/** Расстояние по прямой, км (гаверсинус). */
export function distanceKm(a: Spot, b: Spot): number {
  const R = 6371;
  const rad = (x: number) => (x * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** «1,2 km» (AZ/RU — запятая), «1.2 km» (EN); ближе километра — «650 m». */
export function formatKm(km: number, lang: "az" | "ru" | "en"): string {
  if (km < 1) return `${Math.max(50, Math.round((km * 1000) / 50) * 50)} m`;
  const v = km < 10 ? km.toFixed(1) : String(Math.round(km));
  return `${lang === "en" ? v : v.replace(".", ",")} km`;
}

/** Район справочника по названию из Google (sublocality_level_1 на азербайджанском). */
export function districtFromGoogle(azName: string | null): string | null {
  if (!azName) return null;
  const n = azName.trim().toLowerCase();
  return DISTRICTS.find((d) => d.az.toLowerCase() === n)?.code ?? null;
}

/** n ближайших точек к родителю — чтобы кадр карты показал его вместе с ними. Порядок списка не трогает. */
export function nearestSpots(me: Spot, spots: readonly Spot[], n = 3): Spot[] {
  return spots
    .map((s) => ({ s: { lat: s.lat, lng: s.lng }, km: distanceKm(me, s) }))
    .sort((a, b) => a.km - b.km)
    .slice(0, n)
    .map((x) => x.s);
}

export type Placed = { p: PublicPartner; km: number | null };

/**
 * Порядок списка при известном местоположении.
 *   · у анкеты есть точка — расстояние, ближние выше;
 *   · «выезд по районам» без точки — без расстояния, в списке, если родитель
 *     в их районе (район не определился — тоже в списке: убрать нечем), ниже
 *     анкет с расстоянием;
 *   · онлайн-анкеты сюда не попадают — у них своя вкладка.
 * Местоположения нет — порядок прежний, расстояний нет.
 */
export function placeByDistance(rows: readonly PublicPartner[], me: Spot | null, parentDistrict: string | null): Placed[] {
  if (!me) return rows.map((p) => ({ p, km: null }));
  const withKm: Placed[] = [];
  const travel: Placed[] = [];
  for (const p of rows) {
    if (p.lat !== null && p.lng !== null) withKm.push({ p, km: distanceKm(me, { lat: p.lat, lng: p.lng }) });
    else if (p.address_mode === "travel") {
      if (!parentDistrict || p.travel_districts.includes(parentDistrict)) travel.push({ p, km: null });
    } else withKm.push({ p, km: null });
  }
  withKm.sort((a, b) => (a.km ?? Infinity) - (b.km ?? Infinity));
  return [...withKm, ...travel];
}
